/**
 * Macro #24 — Profile ▸ Activity (first-class cockpit tab).
 *
 * Complete chronological, server-authoritative event history for the wallet:
 * newest first, read/unread state, mark-one (on open) + mark-all, simple V1
 * category filters (All | Rewards | Land | Account), bounded incremental
 * loading, and explicit loading / empty / error states. Read/unread is
 * authoritative from the server (never localStorage).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  activityApi,
  type ActivityCategory,
  type ActivityEvent,
} from "../../lib/activity-api";
import { notifyActivityRead } from "../useActivityNotifications";
import { CATEGORY_TINT, gotoDeepLink, timeAgo } from "../activity-format";
import { Card } from "./primitives";
import { useI18n } from "../../i18n";

type Filter = "ALL" | ActivityCategory;

const FILTERS: Filter[] = ["ALL", "REWARDS", "LAND", "ARTWORK", "ACCOUNT"];

export function ActivityPanel() {
  const { t } = useI18n();
  const [filter, setFilter] = useState<Filter>("ALL");
  const [items, setItems] = useState<ActivityEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const reqRef = useRef(0);

  const load = useCallback(async (f: Filter) => {
    const rid = ++reqRef.current;
    setLoading(true);
    setError(false);
    try {
      const res = await activityApi.history({ category: f === "ALL" ? null : f });
      if (rid !== reqRef.current) return;
      setItems(res.items);
      setCursor(res.next_cursor);
      setUnread(res.unread_count);
    } catch {
      if (rid !== reqRef.current) return;
      setError(true);
      setItems([]);
      setCursor(null);
    } finally {
      if (rid === reqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filter);
  }, [filter, load]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await activityApi.history({
        category: filter === "ALL" ? null : filter,
        cursor,
      });
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.next_cursor);
      setUnread(res.unread_count);
    } catch {
      /* keep what we have */
    } finally {
      setLoadingMore(false);
    }
  };

  const onItem = async (ev: ActivityEvent) => {
    if (!ev.read) {
      try {
        await activityApi.markRead(ev.id);
        setItems((prev) => prev.map((it) => (it.id === ev.id ? { ...it, read: true } : it)));
        setUnread((u) => Math.max(0, u - 1));
        notifyActivityRead();
      } catch {
        /* ignore */
      }
    }
    gotoDeepLink(ev);
  };

  const onMarkAll = async () => {
    try {
      await activityApi.markAllRead();
      setItems((prev) => prev.map((it) => ({ ...it, read: true })));
      setUnread(0);
      notifyActivityRead();
    } catch {
      /* ignore */
    }
  };

  return (
    <Card
      title={t("profile.activity.title")}
      testId="activity-panel"
      right={
        unread > 0 ? (
          <button
            type="button"
            data-testid="activity-mark-all"
            onClick={onMarkAll}
            className="font-mono text-[10px] uppercase tracking-[0.12em] text-teal-200 hover:text-teal-100"
          >
            {t("profile.activity.markAll", { n: unread })}
          </button>
        ) : undefined
      }
    >
      <div data-testid="activity-filters" className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((id) => (
          <button
            key={id}
            type="button"
            data-testid={`activity-filter-${id.toLowerCase()}`}
            data-active={filter === id}
            onClick={() => setFilter(id)}
            className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors ${
              filter === id ? "bg-white/10 text-white" : "text-white/45 hover:text-white/70"
            }`}
          >
            {t("profile.activity.filters." + id.toLowerCase())}
          </button>
        ))}
      </div>

      {loading ? (
        <div data-testid="activity-loading" className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-xl border border-white/5 bg-white/[0.02]" />
          ))}
        </div>
      ) : error ? (
        <div data-testid="activity-error" className="rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center">
          <p className="text-[13px] text-white/70">{t("profile.activity.loadError")}</p>
          <button
            type="button"
            data-testid="activity-retry"
            onClick={() => void load(filter)}
            className="mt-3 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white/75 hover:bg-white/5"
          >
            {t("profile.activity.retry")}
          </button>
        </div>
      ) : items.length === 0 ? (
        <div data-testid="activity-empty" className="rounded-xl border border-white/10 bg-white/[0.02] p-8 text-center text-[13px] text-white/50">
          {t("profile.activity.empty")}
        </div>
      ) : (
        <ul data-testid="activity-list" className="space-y-2">
          {items.map((ev) => (
            <li key={ev.id}>
              <button
                type="button"
                data-testid="activity-item"
                data-read={ev.read}
                data-category={ev.category}
                onClick={() => void onItem(ev)}
                className={`block w-full rounded-xl border p-3 text-left transition-colors hover:bg-white/[0.04] ${
                  ev.read ? "border-white/[0.06] bg-white/[0.01] opacity-70" : "border-white/12 bg-white/[0.03]"
                }`}
              >
                <div className="flex items-start gap-3">
                  {!ev.read && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neon-coral" aria-hidden />}
                  <div className={`min-w-0 flex-1 ${ev.read ? "pl-3.5" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em] ${CATEGORY_TINT[ev.category]}`}>
                        {ev.category}
                      </span>
                      <span className="shrink-0 font-mono text-[10px] text-white/40">{timeAgo(ev.event_time)}</span>
                    </div>
                    <div className="mt-1 text-[14px] font-medium text-white/90">{ev.title}</div>
                    <div className="text-[12px] text-white/55">{ev.body}</div>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!loading && !error && cursor && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            data-testid="activity-load-more"
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="rounded-full border border-white/15 px-5 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-white/75 hover:bg-white/5 disabled:opacity-50"
          >
            {loadingMore ? t("profile.activity.loading") : t("profile.activity.loadMore")}
          </button>
        </div>
      )}
    </Card>
  );
}
